import { CATEGORIES, categoryLabel, elementsLabel, targetSummary } from '../shared/domain';
import type { ReportStatus } from '../shared/status';
import {
  deleteSpot,
  downloadReportPdf,
  fetchPeople,
  fetchSpot,
  reviewReport,
  isOffline,
  saveSpot,
  uploadPhoto,
  type AuthUser,
  type Person,
  type Spot,
} from './api';
import { referenceChips } from './library';
import { dropQueue, enqueue, type PendingPhoto } from './offline';
import { buildTargets, peopleField } from './people';
import { photoField } from './photos';
import { emptyPlace, locationField, type PlaceValue } from './place';
import { photoGallery } from './photos';
import {
  bylines,
  el,
  fmtTime,
  formatPlace,
  fromLocalInput,
  go,
  statusPill,
  textInput,
  toast,
  toLocalInput,
} from './ui';

const PRESELECT = 'mdrc-preselect';

export function renderSpot(
  root: HTMLElement,
  opts: { user: AuthUser; spotId?: string }
): () => void {
  const photos = photoField();
  let people: Person[] = [];
  let existing: Spot | null = null;
  let clientId: string = crypto.randomUUID();
  let references: string[] = [];
  let alive = true;

  const when = textInput({ type: 'datetime-local', value: toLocalInput(new Date()) });
  const who = peopleField([]);
  const place = locationField();
  let category = '';
  const categoryRow = el('div', { className: 'chips' });
  const narrative = el('textarea', {
    className: 'input',
    rows: '5',
    placeholder: 'What did you see?',
  }) as HTMLTextAreaElement;
  const flash = el('input', { type: 'checkbox' }) as HTMLInputElement;
  const error = el('p', { className: 'form-error' });
  const save = el('button', { type: 'button', className: 'btn', textContent: 'Save' });
  const submit = el('button', {
    type: 'button',
    className: 'btn btn-primary',
    textContent: 'Submit',
  });

  function syncFlash(): void {
    if (category === 'incident') {
      flash.checked = true;
      flash.disabled = true;
    } else {
      flash.disabled = false;
    }
  }

  function paintCategories(): void {
    categoryRow.replaceChildren();
    for (const item of CATEGORIES) {
      const on = item.id === category;
      const danger = item.id === 'incident';
      const btn = el('button', {
        type: 'button',
        className: danger ? (on ? 'chip danger on' : 'chip danger') : on ? 'chip on' : 'chip',
        textContent: item.chip,
        'aria-pressed': on ? 'true' : 'false',
      });
      btn.addEventListener('click', () => {
        category = item.id;
        syncFlash();
        paintCategories();
      });
      categoryRow.append(btn);
    }
  }

  function placeValue(): PlaceValue {
    return place.get();
  }

  function problem(status: 'draft' | 'pending'): string {
    if (!when.value) return 'Set the time';
    if (!who.getElements().length) return 'Pick an element';
    if (status === 'draft') return '';
    if (!who.getTokens().length) return 'Pick people or All for an element';
    const here = placeValue();
    const hasCoords = here.lat !== '' && here.lng !== '';
    if (!hasCoords && !here.description) return 'Capture a location or describe the place';
    if ((here.lat === '') !== (here.lng === '')) return 'Location needs both coordinates';
    if (!category) return 'Pick an activity';
    if (!narrative.value.trim()) return 'Add what you saw';
    return '';
  }

  function payload(keys: string[], status: 'draft' | 'pending'): Record<string, unknown> {
    const here = placeValue();
    return {
      client_id: clientId,
      observed_at: fromLocalInput(when.value),
      element: who.getElement(),
      elements: who.getElements(),
      targets: buildTargets(people, who.getTokens()),
      location_lat: here.lat === '' ? null : Number(here.lat),
      location_lng: here.lng === '' ? null : Number(here.lng),
      location_accuracy_m: here.accuracy === '' ? null : Number(here.accuracy),
      location_description: here.description,
      category,
      narrative: narrative.value.trim(),
      flash: category === 'incident' ? true : flash.checked,
      status,
      evidence_keys: keys,
      references,
      id: existing?.id,
    };
  }

  async function onSubmit(status: 'draft' | 'pending'): Promise<void> {
    const message = problem(status);
    error.textContent = message;
    if (message) return;
    save.disabled = true;
    submit.disabled = true;
    const active = status === 'pending' ? submit : save;
    const previous = active.textContent;
    active.textContent = 'Saving…';
    const pending = photos.pending();
    const keys = [...photos.keptKeys()];
    const remaining: PendingPhoto[] = [];
    let failed: unknown = null;
    for (const photo of pending) {
      if (failed) {
        remaining.push(photo);
        continue;
      }
      try {
        keys.push(
          await uploadPhoto(
            new Blob([photo.data], { type: photo.contentType }),
            photo.contentType,
            photo.filename
          )
        );
      } catch (err) {
        failed = err;
        remaining.push(photo);
      }
    }
    const body = payload(keys, status);
    try {
      if (!failed) {
        await saveSpot(body, existing?.id);
        if (existing) await dropQueue(`spot-patch:${existing.id}`);
        else await dropQueue(`spot:${clientId}`);
        toast(status === 'draft' ? 'Saved' : existing ? 'Submitted for review' : 'Submitted for review', 'ok');
        if (status === 'pending' && existing) go(`board/spot/${existing.id}`);
        else if (status === 'pending') reset();
        return;
      }
      throw failed;
    } catch (err) {
      photos.setExisting(keys);
      photos.setPending(remaining);
      const queueId = existing ? `spot-patch:${existing.id}` : `spot:${clientId}`;
      await enqueue({
        id: queueId,
        kind: existing ? 'spot_patch' : 'spot_create',
        payload: body,
        photos: remaining.length ? remaining : failed ? pending : [],
        createdAt: new Date().toISOString(),
        status: 'pending',
        lastError: (err as Error).message,
      });
      if (isOffline(err) || (err as { status?: number }).status === 0) {
        toast('Saved on this phone. It will show on the board when it sends.', 'ok');
      } else {
        toast(`${(err as Error).message}. Saved on this phone.`, 'info');
      }
      if (!existing && status === 'pending') reset();
    } finally {
      if (alive) {
        save.disabled = false;
        submit.disabled = false;
        save.textContent = 'Save';
        submit.textContent = 'Submit';
        if (active.textContent === 'Saving…') active.textContent = previous;
      }
    }
  }

  function reset(): void {
    existing = null;
    clientId = crypto.randomUUID();
    when.value = toLocalInput(new Date());
    who.set('', []);
    place.set(emptyPlace());
    category = '';
    narrative.value = '';
    flash.checked = false;
    photos.setExisting([]);
    photos.setPending([]);
    error.textContent = '';
    references = [];
    paintCategories();
    const pre = sessionStorage.getItem(PRESELECT);
    if (pre) {
      sessionStorage.removeItem(PRESELECT);
      const person = people.find((p) => `person:${p.id}` === pre);
      if (person) who.set(person.element, [pre]);
    }
  }

  function applySpot(spot: Spot): void {
    existing = spot;
    clientId = spot.client_id || clientId;
    when.value = toLocalInput(new Date(spot.observed_at));
    who.set(spot.elements?.length ? spot.elements : spot.element, spot.targets.tokens || []);
    place.set({
      lat: spot.location_lat == null ? '' : String(spot.location_lat),
      lng: spot.location_lng == null ? '' : String(spot.location_lng),
      accuracy:
        spot.location_accuracy_m == null ? '' : String(spot.location_accuracy_m),
      description: spot.location_description || '',
    });
    category = spot.category;
    narrative.value = spot.narrative;
    flash.checked = spot.flash || spot.category === 'incident';
    syncFlash();
    photos.setExisting(spot.evidence_keys || []);
    references = spot.references || [];
    paintCategories();
  }

  save.addEventListener('click', () => void onSubmit('draft'));
  submit.addEventListener('click', () => void onSubmit('pending'));
  paintCategories();

  root.append(
    el('h1', { textContent: opts.spotId ? 'Edit spot report' : 'Spot report' }),
    el('label', { className: 'field' }, [
      el('span', { className: 'field-label', textContent: 'When' }),
      when,
    ]),
    who.element,
    place.element,
    el('div', { className: 'block' }, [
      el('span', { className: 'field-label', textContent: 'Activity' }),
      categoryRow,
    ]),
    el('label', { className: 'field' }, [
      el('span', { className: 'field-label', textContent: 'What happened' }),
      narrative,
    ]),
    el('label', { className: 'switch' }, [
      flash,
      el('span', { textContent: 'Flash to EXCON' }),
    ]),
    photos.element,
    error,
    el('div', { className: 'action-bar' }, [save, submit])
  );

  void (async () => {
    try {
      people = await fetchPeople();
      if (!alive) return;
      who.setPeople(people);
    } catch {
      /* roster fills in when the signal returns; chips stay empty */
    }
    if (opts.spotId) {
      try {
        const spot = await fetchSpot(opts.spotId);
        if (!alive) return;
        if (spot.author_id !== opts.user.id && opts.user.role !== 'admin') {
          error.textContent = 'You can only edit your own report';
          submit.disabled = true;
        }
        applySpot(spot);
      } catch (err) {
        error.textContent = (err as Error).message;
      }
    } else {
      reset();
    }
  })();

  return () => {
    alive = false;
    photos.dispose();
  };
}

export function spotDetail(
  root: HTMLElement,
  opts: { user: AuthUser; id: string }
): () => void {
  const host = el('div', { className: 'stack' });
  root.append(host);
  host.append(el('p', { className: 'muted', textContent: 'Loading…' }));
  let alive = true;
  async function paint(): Promise<void> {
    try {
      const spot = await fetchSpot(opts.id);
      if (!alive) return;
      host.replaceChildren(
        el('p', { className: 'kicker' }, [
          statusPill(spot.status),
          spot.flash ? el('span', { className: 'pill danger', textContent: 'Flash' }) : '',
          el('span', { textContent: fmtTime(spot.observed_at) }),
        ]),
        el('h1', { textContent: categoryLabel(spot.category) }),
        el('p', { className: 'lead', textContent: targetSummary(spot.targets) || 'No people tagged' }),
        el('p', { textContent: spot.narrative }),
        el('p', {
          className: 'muted',
          textContent: [
            elementsLabel(spot.elements, spot.element),
            formatPlace(spot.location_lat, spot.location_lng, spot.location_description) ||
              'No place',
          ]
            .filter(Boolean)
            .join(' · '),
        }),
        ...bylines(spot),
        photoGallery(spot.evidence_keys),
        spot.references?.length
          ? el('div', { className: 'block' }, [
              el('span', { className: 'field-label', textContent: 'References' }),
              referenceChips(spot.references),
            ])
          : ''
      );
      const exportBtn = el('button', { type: 'button', className: 'btn', textContent: 'Export PDF' });
      exportBtn.addEventListener('click', () => {
        void downloadReportPdf('spots', spot.id).catch((err) => toast((err as Error).message, 'err'));
      });
      host.append(exportBtn);
      if (opts.user.role === 'admin') host.append(...reviewActions('spots', spot.id, spot.status, paint));
      if (spot.author_id === opts.user.id || opts.user.role === 'admin') {
        const edit = el('button', {
          type: 'button',
          className: 'btn',
          textContent: 'Edit',
        });
        edit.addEventListener('click', () => go(`spot/${spot.id}`));
        let armed = false;
        const remove = el('button', { type: 'button', className: 'btn btn-ghost', textContent: 'Delete' });
        remove.addEventListener('click', () => {
          if (!armed) {
            armed = true;
            remove.textContent = 'Tap again to delete';
            return;
          }
          void deleteSpot(spot.id)
            .then(() => go('board'))
            .catch((err) => toast((err as Error).message, 'err'));
        });
        host.append(edit, remove);
      }
    } catch (err) {
      if (alive) host.replaceChildren(el('p', { className: 'form-error', textContent: (err as Error).message }));
    }
  }
  void paint();
  return () => {
    alive = false;
  };
}

function reviewActions(
  kind: 'spots' | 'lanes',
  id: string,
  status: ReportStatus,
  onDone: () => Promise<void>
): HTMLButtonElement[] {
  const specs: { label: string; review: 'returned' | 'approved'; action: string }[] = [
    { label: 'Return for Corrections', review: 'returned', action: 'return' },
    { label: 'Approved', review: 'approved', action: 'approve' },
  ];
  return specs.map(({ label, review, action }) => {
    const btn = el('button', {
      type: 'button',
      className: 'btn',
      textContent: label,
      'data-action': action,
    });
    if (status === review) btn.disabled = true;
    btn.addEventListener('click', () => {
      btn.disabled = true;
      void reviewReport(kind, id, review)
        .then(() => {
          toast(review === 'approved' ? 'Approved' : 'Returned for corrections', 'ok');
          return onDone();
        })
        .catch((err) => {
          btn.disabled = false;
          toast((err as Error).message, 'err');
        });
    });
    return btn;
  });
}
